import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { store } from "./store";
import {
  selectTherapistId,
  selectTherapistName,
  selectClinicId,
  selectClinicProfile,
} from "./store/slices/authSlice";

const rootElement = document.getElementById("root");
const appJsx = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// /login and /signup must never hydrate: their reCAPTCHA container has to be
// empty when RecaptchaVerifier attaches, and stale prerendered HTML (old deploys
// cached by browsers) triggers React #418 + "reCAPTCHA placeholder element must
// be empty", blocking login. Discard any baked HTML and render fresh.
const NO_HYDRATE_PATHS = ["/login", "/signup"];
// Also skip hydration when the pre-paint auth boot guard is active (index.html
// sets data-ck-auth-boot for a previously-authenticated user on /, /login,
// /signup). The prerendered HTML is the public landing page, but React will
// render a loader → guaranteed hydration mismatch (React #418). Render fresh.
const isAuthBoot =
  document.documentElement.getAttribute("data-ck-auth-boot") === "1";
if (
  (NO_HYDRATE_PATHS.includes(window.location.pathname) || isAuthBoot) &&
  rootElement.hasChildNodes()
) {
  rootElement.innerHTML = "";
}

const isPrerendered = rootElement.hasChildNodes() && !document.getElementById('app-initial-loader');
if (isPrerendered) {
  ReactDOM.hydrateRoot(rootElement, appJsx);
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(appJsx);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Intercept console.error calls with CK- codes and log them to Firestore
const originalConsoleError = console.error;
const CK_CODE_REGEX = /\[CK-(\d+)\]/;
let logErrorToNivFn = null;

import("./services/nivFirebase").then((mod) => {
  logErrorToNivFn = mod.logErrorToNiv;
});

let _isLoggingToNiv = false;

console.error = (...args) => {
  originalConsoleError.apply(console, args);
  if (_isLoggingToNiv) return;
  try {
    const firstArg = typeof args[0] === "string" ? args[0] : "";
    const match = firstArg.match(CK_CODE_REGEX);
    if (match && logErrorToNivFn) {
      const errorCode = `CK-${match[1]}`;
      const errorData = typeof args[1] === "object" && args[1] ? args[1] : {};
      // Current identity as fallback when the caller didn't pass it explicitly
      let state = {};
      try { state = store.getState(); } catch { state = {}; }
      const therapistId = errorData.therapistId || (state.auth ? selectTherapistId(state) : null) || null;
      const therapistName = errorData.therapistName || (state.auth ? selectTherapistName(state) : null) || null;
      const clinicId = errorData.clinicId || (state.auth ? selectClinicId(state) : null) || null;
      const clinicName =
        errorData.clinicName ||
        (state.auth ? selectClinicProfile(state)?.name : null) ||
        null;
      // Grab a stack: prefer one passed in, else capture the current call site
      let stack = errorData.stack || null;
      if (!stack) {
        const errArg = args.find((a) => a instanceof Error);
        stack = errArg?.stack || null;
      }
      const route =
        typeof window !== "undefined" && window.location
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : null;
      _isLoggingToNiv = true;
      // Some call sites pass `error:` (string or Error) instead of
      // `errorMessage:` — recover the real message so the server-side SMS
      // message filter sees the actual error, not the console title.
      const recoveredMessage =
        errorData.errorMessage ||
        (typeof errorData.error === "string"
          ? errorData.error
          : errorData.error?.message) ||
        null;
      logErrorToNivFn({
        errorCode,
        errorMessage: recoveredMessage || firstArg.slice(0, 200),
        clinicId,
        clinicName,
        context: errorData.timestamp || new Date().toISOString(),
        route,
        therapistId,
        therapistName,
        patientId: errorData.patientId || errorData.entityId || null,
        stack,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        severity: errorData.severity || "error",
      }).finally(() => { _isLoggingToNiv = false; });
    }
  } catch {
    _isLoggingToNiv = false;
  }
};

// Register service worker for PWA support (critical for iOS Home Screen persistence)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.error("[CK-SW] Service worker registration failed:", err);
    });
  });
}

// Request persistent storage so the browser won't evict auth tokens or cached data
if (navigator.storage && navigator.storage.persist) {
  navigator.storage.persist().then((granted) => {
    if (granted) {
    }
  });
}
